import * as React from 'react'
import { assign, find, isFinite, isUndefined, last, split } from 'lodash'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useFlag } from 'toggled'

import { Meta } from '~/components'
import { FiltersProvider } from '~/components/filters/components/filters-provider/filters-provider'
import { getProductFiltersQueryKey } from '~/components/filters/filters-queries'
import type { HTTPError } from '~/errors'
import { categoryQuery } from '~/gql/queries'
import { useVendor } from '~/hooks'
import { jsonLdScriptProps } from '~/lib/json-ld-script-props'
import SearchScreen from '~/screens/search'
import SearchBetaScreen from '~/screens/search-beta'
import { getFixedSerializedFilters } from '~/screens/search-beta/utils'
import type { ICategory, ICategoryMixin, ISubCategory } from '~/types/category'
import type { IProductFilterQueryData } from '~/types/product-filters'
import { getMetaInfo, queryString } from '~/utils'
import { flags, httpStatusCodes } from '~/utils/constants'
import type { GetMetaInfoResult } from '~/utils/get-meta-info'
import { type BreadcrumbItem, createWebPageSchema } from '~/utils/get-schemas'
import isBrowser from '~/utils/is-browser'

import ErrorPage from './_error'

export interface SearchPageProps {
  metaInfo?: GetMetaInfoResult
  page?: number
  category?: ICategoryMixin
  subcategory?: ICategoryMixin
  term?: string
  isParentCategory?: boolean
  initialFilters?: any
  serializedInitialFilters?: string[]
  serializedFixedInitialFilters?: string[]
  sortBy?: string
  brandName?: string
}

interface SearchPageQuery {
  page: string
  term: string
  category: string
  subcategory: string
  sortBy: string
}

function SearchPage(props: SearchPageProps) {
  const hasAdvancedFiltersFF = useFlag(flags.ADVANCED_FILTERS)

  const vendor = useVendor()

  const router = useRouter()

  if (!props.category && !props.term) {
    return <ErrorPage statusCode={httpStatusCodes.NOT_FOUND} />
  }

  const breadcrumbs: BreadcrumbItem[] = []

  if (props.category) {
    breadcrumbs.push({
      name: props.category.name,
      ...(props.subcategory && { url: `/c/${props.category.slug}-${props.category.encodedId}` }),
    })

    if (props.subcategory) {
      breadcrumbs.push({
        name: props.subcategory.name,
      })
    }
  }

  const schema = createWebPageSchema(vendor, router.asPath, breadcrumbs)

  if (hasAdvancedFiltersFF) {
    return (
      <>
        <Meta {...props.metaInfo} />
        <Head>
          <script {...jsonLdScriptProps(schema)} />
        </Head>
        <FiltersProvider
          sortBy={props.sortBy}
          term={props.term}
          serializedInitialFilters={props.serializedInitialFilters}
          serializedFixedInitialFilters={props.serializedFixedInitialFilters}
          /**
           * We need to re-mount when the fixed-filters and term were changed
           * @url https://github.com/facebook/react/issues/14830#issuecomment-535546574
           */
          key={[...props.serializedFixedInitialFilters, props.term].filter(Boolean).join(',')}
        >
          <SearchBetaScreen
            metaInfo={props.metaInfo}
            isCategoryPage={props.isParentCategory}
            page={props.page}
            categoryName={props.category?.name}
            subcategoryName={props.subcategory?.name}
          />
        </FiltersProvider>
      </>
    )
  }

  return (
    <>
      <Meta {...props.metaInfo} />
      <Head>
        <script {...jsonLdScriptProps(schema)} />
      </Head>
      <SearchScreen
        key={[...props.serializedFixedInitialFilters, props.term].filter(Boolean).join(',')}
        metaInfo={props.metaInfo}
        isParentCategory={props.isParentCategory}
        initialFilters={props.initialFilters}
        term={props.term}
        categoryName={props.category?.name}
        subcategoryName={props.subcategory?.name}
      />
    </>
  )
}

const getCategoryMetaInfo = async (
  category: ICategoryMixin,
  subcategory: ICategoryMixin,
  context: PageContext<SearchPageQuery>,
) => {
  const metaInfo = getMetaInfo(context)

  function getCategoryPageMetaInfo() {
    let title = category.name

    if (subcategory) {
      title = `${title} - ${subcategory.name}`
    }

    return {
      title: category?.seoTitle ?? `${title} | ${context.vendor.name}`,
      description:
        category?.seoDescription ??
        category?.description ??
        `Encuentra la mayor variedad de ${category.name} al mejor precio`,
    }
  }

  const overrides = getCategoryPageMetaInfo()

  return assign(metaInfo, overrides)
}

const getInitialFilters = (params: { category?: ICategory; subcategory?: ISubCategory }) => {
  const { category, subcategory } = params

  if (subcategory) {
    return {
      category: category.name,
      subcategory: subcategory.name,
    }
  }

  return {
    category: category.name,
  }
}

const getCategoryAndSubcategory = (categoryData, subcategoryId?) => {
  const category: ICategoryMixin = categoryData.vendor.category

  const { subcategories } = category

  let subcategory: ICategoryMixin

  if (subcategoryId) {
    subcategory = find(subcategories, { encodedId: subcategoryId }) as ICategoryMixin
  }

  return {
    category,
    subcategory,
  }
}

SearchPage.getInitialProps = async (context: PageContext<SearchPageQuery>): Promise<SearchPageProps> => {
  const { query } = context

  let page: number

  if (query.term) {
    assign(query, {
      term: decodeURIComponent(query.term),
    })
  }

  const queryStrings = context.asPath.split('?')[1]

  const serializedInitialFilters = (queryString.parse(queryStrings).filters ?? []).sort()

  if (isFinite(query.page)) {
    page = Number(query.page)
  }

  if (context.asPath.startsWith('/b/')) {
    return {
      page,
      term: query.term,
      metaInfo: getMetaInfo(context),
      serializedFixedInitialFilters: [],
    }
  }

  const categoryId = last(split(query.category, '-'))

  const subcategoryId = last(split(query.subcategory, '-'))

  let categoryData

  try {
    const result = await context.apolloClient.query({
      query: categoryQuery,
      variables: {
        encodedId: categoryId,
      },
    })
    categoryData = result.data
  } catch (error) {
    console.error('Category query failed:', error)
  }

  if (!categoryData?.vendor?.category) {
    if (context.res) {
      context.res.statusCode = httpStatusCodes.NOT_FOUND
    }

    return {
      category: null,
      term: null,
    }
  }

  const { category, subcategory } = getCategoryAndSubcategory(categoryData, subcategoryId)

  const categoryName = category.name

  const subcategoryName = subcategory?.name

  const hasAdvancedFiltersFF = context.hasFlag(flags.ADVANCED_FILTERS)

  const serializedFixedInitialFilters = getFixedSerializedFilters({ categoryName, subcategoryName })

  const productFiltersQueryKey = getProductFiltersQueryKey(serializedFixedInitialFilters, query.term)

  const commonProps = {
    page,
    sortBy: query.sortBy,
    serializedInitialFilters,
    serializedFixedInitialFilters,
  }

  if (hasAdvancedFiltersFF && !isBrowser) {
    await context.queryClient.prefetchQuery<IProductFilterQueryData, HTTPError>(productFiltersQueryKey, {
      staleTime: Infinity,
    })
  }

  return {
    ...commonProps,
    category: category,
    subcategory: subcategory,
    metaInfo: await getCategoryMetaInfo(category, subcategory, context),
    isParentCategory: isUndefined(query.subcategory),
    initialFilters: getInitialFilters({ category, subcategory }),
  }
}

export default SearchPage
